import { useEffect, useState } from "react";
import { Box, Grid, Typography, Paper, IconButton } from "@mui/material";
import {
  ArrowUpward,
  ArrowDownward,
  Delete,
  Add,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { toast } from "react-toastify";

/* =========================
   WIDGET DEFINITIONS
========================= */

interface Widget {
  id: string;
  title: string;
  description: string;
  group: string;
}

export const ALL_WIDGETS: Widget[] = [
  // ============ ANALYTICS ============
  {
    id: "creator_insights",
    title: "Creator Insights (Volume vs Quality)",
    description: "Shows venues contributed vs average quality score",
    group: "ANALYTICS",
  },
  {
    id: "speed_by_type",
    title: "Speed by Venue Type",
    description: "Internet upload speed grouped by venue type",
    group: "ANALYTICS",
  },
  {
    id: "capacity_speed_quality",
    title: "Capacity vs Speed vs Quality",
    description: "Relationship between capacity, speed, and quality",
    group: "ANALYTICS",
  },
  {
    id: "top_10_contributors",
    title: "Top 10 Contributors",
    description:
      "Shows the top 10 contributors based on venues added or activity",
    group: "ANALYTICS",
  },
  {
    id: "isp_performance",
    title: "ISP Performance",
    description: "Average upload speed grouped by network providers",
    group: "ANALYTICS",
  },
  {
    id: "capacity_speed_heatmap",
    title: "Capacity vs Speed Heatmap",
    description:
      "Visual heatmap showing capacity vs internet speed across venues",
    group: "ANALYTICS",
  },

  // ============ SUMMARY ============
  {
    id: "average_speed",
    title: "Average Internet Speed",
    description: "Average upload speed across venues",
    group: "SUMMARY",
  },
  {
    id: "average_score",
    title: "Average Venue Score",
    description: "Average performance score across venues",
    group: "SUMMARY",
  },
  {
    id: "venue_types_pie",
    title: "Venue Types Distribution",
    description: "Pie chart showing distribution of venues by type",
    group: "SUMMARY",
  },
  {
    id: "stream_readiness",
    title: "Stream Readiness",
    description: "Shows percentage of venues ready for streaming (true/false)",
    group: "SUMMARY",
  },
  {
    id: "city_suburb_hierarchy",
    title: "City/Suburb Hierarchy",
    description: "Pie chart showing hierarchy of cities and their suburbs",
    group: "SUMMARY",
  },
  {
    id: "top_surburbs_by_capacity",
    title: "Top Suburbs by Capacity",
    description: "Top surburbs by their capacity",
    group: "SUMMARY",
  },
  // ============ COMPARISON ============
  {
    id: "total_capacity_by_venue",
    title: "Total Capacity by Venue Type",
    description: "Compare venues by amenities and capacity",
    group: "COMPARISON",
  },
  {
    id: "venue_types_by_capacity",
    title: "Venue Types by Capacity",
    description: "Compare capacities across venue types",
    group: "COMPARISON",
  },
  {
    id: "suburb_speed_rank",
    title: "Suburb Speed Rank",
    description: "Rank suburbs by average upload speed",
    group: "COMPARISON",
  },
];

/* =========================
   HELPERS
========================= */

const groupWidgets = (widgets: Widget[]) => {
  return widgets.reduce((acc: Record<string, Widget[]>, widget) => {
    if (!acc[widget.group]) acc[widget.group] = [];
    acc[widget.group].push(widget);
    return acc;
  }, {});
};

/* =========================
   COMPONENT
========================= */

const VenuesWidgetAdmin = () => {
  const [activeWidgetIds, setActiveWidgetIds] = useState<string[]>([]);
  const [eyeState, setEyeState] = useState<Record<string, boolean>>({});

  // Load saved widgets from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("venueActiveWidgets");
    if (saved) {
      setActiveWidgetIds(JSON.parse(saved));
    }
  }, []);

  // Save active widgets whenever they change
  useEffect(() => {
    localStorage.setItem("venueActiveWidgets", JSON.stringify(activeWidgetIds));
  }, [activeWidgetIds]);

  const availableWidgets = ALL_WIDGETS.filter(
    (w) => !activeWidgetIds.includes(w.id)
  );

  const groupedAvailable = groupWidgets(availableWidgets);

  /* =========================
     ACTIONS
  ========================= */

  const addWidget = (id: string) => setActiveWidgetIds((prev) => [...prev, id]);

  const removeWidget = (id: string) =>
    setActiveWidgetIds((prev) => prev.filter((w) => w !== id));

  const moveUp = (id: string) =>
    setActiveWidgetIds((prev) => {
      const i = prev.indexOf(id);
      if (i === 0) return prev;
      const copy = [...prev];
      [copy[i - 1], copy[i]] = [copy[i], copy[i - 1]];
      return copy;
    });

  const moveDown = (id: string) =>
    setActiveWidgetIds((prev) => {
      const i = prev.indexOf(id);
      if (i === prev.length - 1) return prev;
      const copy = [...prev];
      [copy[i], copy[i + 1]] = [copy[i + 1], copy[i]];
      return copy;
    });

  const toggleEye = (id: string) => {
    setEyeState((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Widget Administration
      </Typography>

      <Typography variant="body2" sx={{ mb: 3 }}>
        Manage dashboard widgets and their display settings
      </Typography>

      <Grid container spacing={3}>
        {/* ================= LEFT ================= */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Active Widgets
            </Typography>

            {activeWidgetIds.map((id) => {
              const widget = ALL_WIDGETS.find((w) => w.id === id);
              if (!widget) return null;

              const index = activeWidgetIds.indexOf(id);

              return (
                <Paper
                  key={widget.id}
                  sx={{
                    p: 3,
                    mt: 2,
                    backgroundColor: "#fffefeff",
                    boxShadow: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography fontWeight={600}>{widget.title}</Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <IconButton
                        size="small"
                        disabled={index === 0}
                        onClick={() => moveUp(widget.id)}
                      >
                        <ArrowUpward sx={{ fontSize: 14 }} />
                      </IconButton>

                      <IconButton
                        size="small"
                        disabled={index === activeWidgetIds.length - 1}
                        onClick={() => moveDown(widget.id)}
                      >
                        <ArrowDownward sx={{ fontSize: 14 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => toggleEye(widget.id)}
                      >
                        {eyeState[widget.id] !== false ? (
                          <Visibility sx={{ fontSize: 14 }} />
                        ) : (
                          <VisibilityOff sx={{ fontSize: 14 }} />
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeWidget(widget.id)}
                      >
                        <Delete sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Typography variant="body2">{widget.description}</Typography>
                </Paper>
              );
            })}

            <Box sx={{ mt: 2, textAlign: "right" }}>
              <button
                onClick={() => {
                  localStorage.setItem(
                    "venueActiveWidgets",
                    JSON.stringify(activeWidgetIds)
                  );
                  toast("Widget order saved successfully!");
                }}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Save Order
              </button>
            </Box>
          </Paper>
        </Grid>

        {/* ================= RIGHT ================= */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Available Widget Types
            </Typography>

            {Object.entries(groupedAvailable).map(([group, widgets]) => (
              <Box key={group} sx={{ mb: 1 }}>
                <Typography
                  variant="overline"
                  sx={{ fontWeight: "bold", fontSize: 14 }}
                >
                  {group}
                </Typography>

                {widgets.map((widget) => (
                  <Paper key={widget.id} sx={{ p: 2, mt: 1 }}>
                    <Typography fontWeight={600} fontSize={13}>
                      {widget.title}
                    </Typography>
                    <Typography variant="body2" fontSize={10}>
                      {widget.description}
                    </Typography>

                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <IconButton
                        size="small"
                        onClick={() => addWidget(widget.id)}
                        sx={{
                          width: 28,
                          height: 28,
                          p: 0,
                          borderRadius: 1,
                          bgcolor: "black",
                          color: "white",
                          "&:hover": {
                            bgcolor: "#222",
                          },
                        }}
                      >
                        <Add sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VenuesWidgetAdmin;
